import {
  CardIncident,
  GoalIncident,
  Match,
  MissedGoalIncident,
  NeutralIncident,
  SubstitutionIncident
} from "@/modules/match/entity";
import {MatchResultEnum} from "@/modules/base/enums";
import {inject, injectable} from "inversify";
import {MatchEvaluatorInterface} from "@/modules/competitions/interfaces";
import {Player, Team} from "@/modules/team/entity";
import chance from "@/modules/base/services/chance";
import {CardEnum, NeutralIncidentTypeEnum, SubstitutionTypeEnum} from "@/modules/match/enums";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import LineupSkillCalculator from "@/modules/team/services/LineupSkillCalculator";
import EffectiveLineupSkillCalculator from "@/modules/team/services/EffectiveLineupSkillCalculator";

@injectable()
export default abstract class AbstractMatchEvaluator implements MatchEvaluatorInterface {
  constructor(
    @inject(ConfigRepository) protected configRepository: ConfigRepository,
    @inject(LineupSkillCalculator) protected lineupSkillCalculator: LineupSkillCalculator,
    @inject(EffectiveLineupSkillCalculator) protected effectiveLineupSkillCalculator: EffectiveLineupSkillCalculator,
  ) {}

  abstract evaluate(match: Match): Promise<void>;

  protected async prepare(match: Match) {
    match.homeGoals = 0;
    match.awayGoals = 0;
    match.homeChances = 0;
    match.awayChances = 0;
    match.homePossession = 0;
    match.awayPossession = 0;
    match.homeFormation = match.homeTeam.formation;
    match.awayFormation = match.awayTeam.formation;

    const homeFormation = this.configRepository.get('formations.all')[match.homeTeam.formation];

    match.homeSkill = this.lineupSkillCalculator.calculateLinedupSkill(match.homeTeam.players, homeFormation);
    match.homeEffectiveSkill = this.effectiveLineupSkillCalculator.calculateLinedupSkill(match.homeTeam.players, homeFormation);

    const awayFormation = this.configRepository.get('formations.all')[match.awayTeam.formation];
    match.awaySkill = this.lineupSkillCalculator.calculateLinedupSkill(match.awayTeam.players, awayFormation);
    match.awayEffectiveSkill = this.effectiveLineupSkillCalculator.calculateLinedupSkill(match.awayTeam.players, awayFormation);

    await match.save();
  }

  protected async processResult(match: Match) {
    if (match.homeGoals! > match.awayGoals!) {
      match.result = MatchResultEnum.HOME_WIN;
    } else if (match.homeGoals! < match.awayGoals!) {
      match.result = MatchResultEnum.AWAY_WIN;
    } else {
      match.result = MatchResultEnum.DRAW;
    }

    await match.save();
  }

  protected getMinute() {
    return chance.integer({min: 1, max: 95});
  }

  async createNeutralIncident(match: Match, type: NeutralIncidentTypeEnum, minute?: number): Promise<NeutralIncident> {
    const incident = new NeutralIncident();
    incident.match = match;
    incident.neutralType = type;
    incident.minute = minute ?? this.getMinute();
    await incident.save();

    return incident;
  }

  async createGoalIncident(match: Match, team: Team, oppositeTeam: Team, minute?: number, player?: Player, noOwnGoals: boolean = false): Promise<GoalIncident> {
    const incident = new GoalIncident();
    incident.match = match;
    incident.team = team;
    incident.minute = minute ?? this.getMinute();

    if (!noOwnGoals && chance.bool({likelihood: this.configRepository.get('match.evaluation.chanceOwnGoal') as number})) {
      incident.player = chance.pickone(oppositeTeam.players.filter(p => p.lineup !== null));
      incident.ownGoal = true;
    } else {
      incident.player = player ?? chance.pickone(team.players.filter(p => p.lineup !== null));
    }

    await incident.save();

    return incident;
  }

  async createMissedGoalIncident(match: Match, team: Team, player: Player, minute: number) {
    const incident = new MissedGoalIncident();
    incident.match = match;
    incident.minute = minute;
    incident.player = player;
    incident.team = team;

    await incident.save();

    return incident;
  }

  public async createSubstituteIncident(match: Match, team: Team, playerIn?: Player, playerOut?: Player, minute?: number): Promise<[SubstitutionIncident, SubstitutionIncident]> {
    playerIn = playerIn ?? collect(team.players).filter(p => !p.lineup).random() as Player;
    playerOut = playerOut ?? collect(team.players).filter(p => !!p.lineup).random() as Player;
    minute = minute ?? this.getMinute();

    const incidentOut = new SubstitutionIncident();
    incidentOut.match = match;
    incidentOut.team = team;
    incidentOut.minute = minute;
    incidentOut.player = playerOut;
    incidentOut.substitutionType = SubstitutionTypeEnum.OUT;
    await incidentOut.save();

    const incidentIn = new SubstitutionIncident();
    incidentIn.match = match;
    incidentIn.team = team;
    incidentIn.minute = minute;
    incidentIn.player = playerIn;
    incidentIn.substitutionType = SubstitutionTypeEnum.IN;
    await incidentIn.save();

    return [incidentOut, incidentIn];
  }

  async createCardIncident(match: Match, team: Team, player: Player, card: CardEnum, minute: number): Promise<CardIncident> {
    const incident = new CardIncident();
    incident.match = match;
    incident.team = team;
    incident.minute = minute;
    incident.player = player || collect(team.players).filter(p => !!p.lineup).random() as Player;
    incident.card = card;
    await incident.save();

    return incident;
  }
}